import { useMemo, useState } from "react";
import { extractCustomer } from "@/lib/conversation/customer";
import { isRecordedMeeting } from "@/lib/conversation/recordedMeeting";
import { cn } from "@/lib/utils";
import { Conversation } from "@/types/conversation/conversation";
import { TranscriptLine, TranscriptToolCall, TranscriptToolResult } from "@/types/conversation/transcript";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { parseSharedMedia } from "@/types/voice/genie-attachment";
import { SHOW_RELATED_MEDIA_TOOL_NAME } from "@/utils/config/elevenlabs";
import { GenieAttachmentCard } from "@/pages/agent-interfaces/live-agent/components/GenieAttachmentCard";
import { TranscriptCopyButton } from "./TranscriptCopyButton";

interface TranscriptTabProps {
  transcript: TranscriptLine[];
  /**
   * When provided, renders an in-context "Copy transcript" control in the card
   * header, and names the speakers: the customer by name or email, the genie
   * by its name and logo.
   */
  conversation?: Conversation;
  /** Names the genie on surfaces that hold no Conversation (e.g. conversation logs). */
  genieName?: string;
  /** The genie's logo, shown with `genieName`. */
  genieLogoUrl?: string | null;
}

const SPEAKER_COLORS = [
  { bg: "bg-[#4E9CFF]/10", text: "text-[#4E9CFF]" },
  { bg: "bg-[#FF4DB8]/10", text: "text-[#FF4DB8]" },
  { bg: "bg-amber-100", text: "text-amber-600" },
  { bg: "bg-emerald-100", text: "text-emerald-600" },
];

interface Participant {
  label: string;
  initials: string;
  avatarUrl?: string | null;
}

const initialsOf = (label: string): string => {
  const words = label.split(/[\s@._-]+/).filter(Boolean);
  const letters = words.length > 1 ? words[0][0] + words[1][0] : label.slice(0, 2);
  return letters.toUpperCase();
};

/**
 * Who "user" and "agent" are in a genie conversation: the customer by name or
 * email when the analysis captured one, the genie by its name and logo.
 * Meeting recordings and genie meetings name their own speakers, so they get no mapping.
 */
const conversationParticipants = (conversation?: Conversation): Record<string, Participant> => {
  if (!conversation || isRecordedMeeting(conversation.type)) return {};

  const customer = extractCustomer(conversation);
  const customerLabel = customer.name ?? customer.email ?? "Customer";
  const branding = conversation.agent?.branding;
  const genieLabel = conversation.agent?.name || "Genie";

  return {
    user: { label: customerLabel, initials: initialsOf(customerLabel) },
    agent: {
      label: genieLabel,
      initials: initialsOf(genieLabel),
      avatarUrl: branding?.logoUrl || branding?.customAvatarUrl || null,
    },
  };
};

interface SpeakerAvatarProps {
  label: string;
  initials: string;
  avatarUrl?: string | null;
  className: string;
  textClassName: string;
}

const SpeakerAvatar = ({ label, initials, avatarUrl, className, textClassName }: SpeakerAvatarProps) => {
  const [imageFailed, setImageFailed] = useState(false);

  if (avatarUrl && !imageFailed) {
    return (
      <img
        src={avatarUrl}
        alt={label}
        onError={() => setImageFailed(true)}
        className="w-8 h-8 rounded-full flex-shrink-0 object-contain bg-white border border-gray-200"
      />
    );
  }

  return (
    <div className={cn("w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0", className)}>
      <span className={cn("text-xs font-semibold", textClassName)}>{initials}</span>
    </div>
  );
};

interface TranscriptToolCallsProps {
  toolCalls?: TranscriptToolCall[];
  resultsByRequestId: Map<string, TranscriptToolResult>;
}

/** A turn's tool calls: links and pictures shared on screen as cards, other tools as a name and response. */
const TranscriptToolCalls = ({ toolCalls, resultsByRequestId }: TranscriptToolCallsProps) => {
  if (!toolCalls || toolCalls.length === 0) return null;

  return (
    <div className="mt-2 space-y-2">
      {toolCalls.map((tool, toolIndex) => {
        const toolName = tool.tool_name ?? tool.name;
        const result = resultsByRequestId.get(tool.request_id ?? "");
        // Media shows up either as the tool's own reply or, when the
        // lookup outran the agent, as a later contextual update.
        const sharedMedia =
          toolName === SHOW_RELATED_MEDIA_TOOL_NAME || tool.type === "system"
            ? parseSharedMedia(result?.result_value)
            : null;

        // System tools (contextual updates) carry the injected session
        // context — never shown as a tool call.
        if (!sharedMedia && tool.type === "system") return null;

        if (sharedMedia) {
          return (
            <div key={toolIndex} className="space-y-2">
              <p className="text-xs text-gray-500">Shared on screen</p>
              {sharedMedia.map((attachment) => (
                <GenieAttachmentCard
                  key={`${attachment.kind}:${attachment.url}`}
                  attachment={attachment}
                  brandColor={DEFAULT_BRANDING.primaryColor}
                  variant="dashboard"
                />
              ))}
            </div>
          );
        }

        const response = tool.response ?? result?.result_value;
        return (
          <div key={toolIndex} className="text-xs bg-gray-50 p-2 rounded">
            <p><span className="font-medium">Tool:</span> {toolName}</p>
            {response && <p className="break-words"><span className="font-medium">Response:</span> {response}</p>}
          </div>
        );
      })}
    </div>
  );
};

export const TranscriptTab = ({ transcript, conversation, genieName, genieLogoUrl }: TranscriptTabProps) => {
  const hasTranscript = transcript && transcript.length > 0;
  const participants = useMemo(() => {
    const named = conversationParticipants(conversation);
    if (!genieName || named.agent) return named;
    return {
      ...named,
      agent: { label: genieName, initials: initialsOf(genieName), avatarUrl: genieLogoUrl },
    };
  }, [conversation, genieName, genieLogoUrl]);

  // Client tool results land on a later turn than the call, so pair them by request id.
  const resultsByRequestId = new Map(
    (transcript ?? [])
      .flatMap((line) => line.tool_results ?? [])
      .filter((result) => result.request_id)
      .map((result) => [result.request_id as string, result]),
  );

  // Recording transcripts use { speaker, text } instead of { role, message }
  const isRecordingTranscript = hasTranscript && "speaker" in (transcript[0] as any);

  // Build a stable color map per unique speaker
  const speakerColorMap = (() => {
    if (!isRecordingTranscript) return {};
    const map: Record<string, (typeof SPEAKER_COLORS)[number]> = {};
    let i = 0;
    for (const item of transcript as any[]) {
      if (item.speaker && !(item.speaker in map)) {
        map[item.speaker] = SPEAKER_COLORS[i % SPEAKER_COLORS.length];
        i++;
      }
    }
    return map;
  })();

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 max-h-[60vh] overflow-y-auto">
      {conversation && hasTranscript && (
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="text-base font-semibold text-gray-900">Transcript</h3>
          <TranscriptCopyButton conversation={conversation} transcript={transcript} />
        </div>
      )}
      <div className="space-y-4">
        {hasTranscript ? (
          isRecordingTranscript ? (
            (transcript as any[]).map((item, idx) => {
              const color = speakerColorMap[item.speaker] ?? SPEAKER_COLORS[0];
              const participant = participants[item.speaker];
              const label = participant?.label ?? item.speaker;
              const timeLabel = item.start !== undefined ? `${Math.floor(item.start)}s` : "";
              return (
                <div key={idx} className="flex gap-2 sm:gap-4">
                  <SpeakerAvatar
                    label={label}
                    initials={participant?.initials ?? item.speaker.slice(0, 2).toUpperCase()}
                    avatarUrl={participant?.avatarUrl}
                    className={color.bg}
                    textClassName={color.text}
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-semibold break-words ${color.text}`}>{label}</p>
                      {timeLabel && <span className="text-xs text-gray-400">{timeLabel}</span>}
                    </div>
                    {item.text && <p className="text-sm text-gray-600 break-words">{item.text}</p>}
                    <TranscriptToolCalls toolCalls={item.tool_calls} resultsByRequestId={resultsByRequestId} />
                  </div>
                </div>
              );
            })
          ) : (
            transcript.map((item, idx) => {
              const isUser = item.role === "user";
              const participant = participants[isUser ? "user" : "agent"];
              const label = participant?.label ?? (isUser ? "User" : "Agent");
              return (
                <div key={idx} className={`flex gap-2 sm:gap-4 ${isUser ? "ml-4 sm:ml-8" : ""}`}>
                  <SpeakerAvatar
                    label={label}
                    initials={participant?.initials ?? (isUser ? "U" : "A")}
                    avatarUrl={participant?.avatarUrl}
                    className={isUser ? "bg-blue-100" : "bg-gray-100"}
                    textClassName={isUser ? "text-blue-600" : "text-gray-600"}
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium break-words text-[#222B45]">{label}</p>
                      <span className="text-xs text-gray-400">
                        {item.time_in_call_secs !== undefined ? `${item.time_in_call_secs}s` : ""}
                      </span>
                    </div>
                    {item.message && <p className="text-sm text-gray-600 break-words">{item.message}</p>}
                    <TranscriptToolCalls toolCalls={item.tool_calls} resultsByRequestId={resultsByRequestId} />
                  </div>
                </div>
              );
            })
          )
        ) : (
          <div className="text-center text-gray-400">No transcript available.</div>
        )}
      </div>
    </div>
  );
};
